import {
	DATASOURCE_TYPE,
	Layer as LayerObj,
} from "~map-view/types/__generated/gql/graphql";
import {
	getLabelsLayerId,
	getLabelsStyle,
	getMaplibreStylesFromLayerConfigs,
} from "map-style-utils";
import { useEffect, useMemo, useState } from "react";
import { Case, Default, Switch } from "react-if";
import {
	Layer as MapLibreLayer,
	Source as MaplibreSource,
	useMap,
} from "react-map-gl/maplibre";

export const Layer = (
	props:
		| LayerObj
		| (LayerObj & {
				/** For labels to be displayed correctly */
				allLayers: { id: string }[];
		  }),
) => {
	const { visible, _dataset } = props;

	if (!visible) return null;

	return (
		<Switch>
			<Case condition={_dataset.datasourceType === DATASOURCE_TYPE.GEOTIFF}>
				<GeoTiffSource {...props} />
			</Case>
			<Case condition={_dataset.datasourceType === DATASOURCE_TYPE.WMS}>
				<WmsSource {...props} />
			</Case>
			<Default>
				{/* @ts-ignore */}
				<VectorSource {...props} />
			</Default>
		</Switch>
	);
};

const GeoTiffSource = (props: LayerObj) => {
	const { _dataset } = props;

	return (
		<MaplibreSource
			volatile
			type="raster"
			url={`cog://${process.env.COG_TILES_SERVER_CLOUDFRONT_URL}/datasets/${_dataset.sourceDatasetId}/cog-tiles.tif`}>
			<MapLibreLayer
				id="geotiff"
				type="raster"
			/>
		</MaplibreSource>
	);
};

const WmsSource = ({ _dataset }: LayerObj) => {
	const url = new URL(_dataset.getCapabilitiesUrl!.toLowerCase());
	const tileUrl = `${url.origin}${
		url.pathname
	}?service=WMS&request=GetMap&layers=${
		_dataset.name
	}&styles=&format=image/png&transparent=true&version=${
		url.searchParams.get("version") ?? "1.3.0"
	}&unloadInvisibleTiles=false&tiled=yes&isVisible=true&worldCopyJump=false&width=256&height=256&crs=EPSG:3857&srs=EPSG:3857&bbox={bbox-epsg-3857}`;

	return (
		<MaplibreSource
			volatile
			type="raster"
			tileSize={256}
			tiles={[tileUrl]}>
			<MapLibreLayer
				id="wms"
				type="raster"
			/>
		</MaplibreSource>
	);
};

const VectorSource = (
	props: LayerObj & {
		/** For labels to be displayed correctly */
		allLayers: { id: string }[];
	},
) => {
	const { current: mapRef } = useMap();

	const { id, datasetId, labelsStyle, _dataset, allLayers } = props;

	const dataId = `public.${_dataset.sourceDatasetId}`;

	const layerStyles = useMemo(
		() => getMaplibreStylesFromLayerConfigs(props),
		[props],
	);

	const [beforeId, setBeforeId] = useState<string>();
	useEffect(() => {
		// @ts-ignore
		const firstLayerWithLabels = allLayers.find((layer) => !!layer.labelsStyle);
		const labelLayerId = firstLayerWithLabels
			? getLabelsLayerId(firstLayerWithLabels.id)
			: undefined;
		// const labelLayerId = getLabelsLayerId(allLayers[0].id);
		if (labelLayerId && mapRef?.getLayer(labelLayerId)) {
			setBeforeId(labelLayerId);
		}
	}, [allLayers, mapRef]);

	return (
		<MaplibreSource
			id={datasetId}
			type="vector"
			tiles={[
				`${process.env.TILES_SERVER_CLOUDFRONT_URL}/${dataId}/{z}/{x}/{y}.pbf`,
			]}>
			{layerStyles.map((style) => (
				<MapLibreLayer
					source-layer={dataId}
					beforeId={beforeId}
					{...style}
					key={`${id}-${style.id}`}
					id={`${id}-${style.id}`}
				/>
			))}
			{labelsStyle && (
				<MapLibreLayer
					source-layer={dataId}
					{...getLabelsStyle(id, _dataset.type, labelsStyle)}
				/>
			)}
		</MaplibreSource>
	);
};
